import * as THREE from 'three';
import * as dat from 'lil-gui';


import World from './World.js';
import Camera from './Camera.js';
import Time from './utils/Time.js';
import Sizes from './utils/Sizes.js';
import Renderer from './Renderer.js';
import Resources from './utils/Resources.js';

import UrlParams from '@/core/UrlParams';
import sources from './sources.js';

let instance = null;

export default class Experience {
    constructor(canvas, video) {
        // Singleton
        if (instance) {
            return instance;
        }
        instance = this;
        window.experience = this;

        // Variables
        this.canvas = canvas;
        this.sizes = new Sizes();
        this.time = new Time();

        this.scene = new THREE.Scene();
        this.camera = new Camera();
        this.renderer = new Renderer();

        this.resources = new Resources(sources);

        this.world = new World();

        this.video = video;

        this.init();

        const photoTextureSize = 512;
        this.posterSize = new THREE.Vector2( 764, 1080 );

        this.renderTargetPhoto = new THREE.WebGLRenderTarget(photoTextureSize, photoTextureSize);
        this.renderTargetPoster = new THREE.WebGLRenderTarget(this.posterSize.x, this.posterSize.y);

        this.takePhotoFunction = this.takePhoto.bind(this);
        this.retakePhotoFunction = this.retakePhoto.bind(this);
        this.generatePosterFunction = this.createPoster.bind(this);
        this.URLPoster = null;
        this.namePoster = null;

        this.renderTargetPhoto.texture.colorSpace = THREE.SRGBColorSpace;
        this.renderTargetPoster.texture.colorSpace = THREE.SRGBColorSpace;

        // Resize event
        this.sizes.on('resize', () => {
            this.resize();
        });

        // Time tick event
        this.time.on('tick', () => {
            this.update();
        });
    }

    resize() {
        this.camera.resize();
        this.renderer.resize();
    }

    update() {
        this.camera.update();
        this.renderer.update();
    }

    initWebcam(video) {
        const isMobile = navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i);

        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {

            const constraints = { video: { width: isMobile ? 1080 : 1920, height: isMobile ? 1920 : 1080, facingMode: 'user' } };
            navigator.mediaDevices.getUserMedia(constraints).then(function (stream) {

                // apply the stream to the video element used in the texture
                video.srcObject = stream;
                video.play();

            }).catch(function (error) {

                console.error('Unable to access the camera/webcam.', error);

            });

        } else {

            console.error('MediaDevices interface not available.');

        }
    }

    init() {
        this.initWebcam(this.video);

        const isDebug = UrlParams.getBool('debug');
        if(isDebug && process.env.VUE_APP_ENVCONFIG === "development") {
            this.initGUI();
        }
    }

    initGUI() {
        this.gui = new dat.GUI();
        
        const buttonPhoto = {
            takePhoto: () => {
                this.exportScreenshot();
            },
        };
        this.gui.add(buttonPhoto, "takePhoto").name("Take Photo");

        const buttonPoster = {
            preparePoster: () => {
                this.preparePoster();
            }
        };
        this.gui.add(buttonPoster, "preparePoster").name("Prepare Poster");

        const buttonDownload = {
            download: () => {
                this.downloadImage();
            }
        };
        this.gui.add(buttonDownload, "download").name("Download");

        const retakePhoto = {
            retakePhoto: () => {
                this.retakePhoto();
            }
        };
        this.gui.add(retakePhoto, "retakePhoto").name("Retake Photo");

        this.gui.close();

    }


    retakePhoto() {
        this.renderer.instance.setRenderTarget(null)
        this.world.onRetakePhoto();
    }

    takePhoto() {
        this.exportScreenshot();
    }

    exportScreenshot() {
        //window.removeEventListener('resize', onWindowResize)
        
        this.renderer.onScreenshotTaken(this.renderTargetPhoto);
        this.world.onScreenshotTaken(this.renderTargetPhoto.texture);

        this.renderer.instance.setRenderTarget(null)
    }

    preparePoster(posterName) {
        this.renderer.onPreparePoster();
        this.world.onPreparePoster(posterName);
    }

    downloadImage() {
        this.URLPoster = this.renderer.onDownloadImage(this.renderTargetPoster, this.posterSize, this.canvas)

        //this.sizes.onDownloadImage();
        this.renderer.instance.setRenderTarget(null)
    }

    createPoster(posterName) {
        this.preparePoster(posterName);
        this.downloadImage();
    }

    destroy() {
        this.sizes.off('resize')
        this.time.off('tick')

        this.renderer.destroy();

        this.scene.traverse((child) =>
        {
            // Test if it's a mesh
            if(child instanceof THREE.Mesh)
            {
                child.geometry.dispose()

                // Loop through the material properties
                for(const key in child.material)
                {
                    const value = child.material[key]

                    // Test if there is a dispose function
                    if(value && typeof value.dispose === 'function')
                    {
                        value.dispose()
                    }
                }
            }
        })

        this.renderTargetPhoto.dispose();
        this.renderTargetPoster.dispose();

        this.video.pause();

        instance = null;

        const isDebug = UrlParams.getBool('debug');
        if(isDebug && process.env.VUE_APP_ENVCONFIG === "development") {
            if(this.gui.active)
                this.gui.ui.destroy()
        }
    }

}